import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../config';
import './JobSeekerProfileSetup.css';

function JobSeekerProfileSetup() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    full_name: '',
    phone: '',
    city: '',
    province: '',
    professional_title: '',
    
    // Step 2: Professional Background
    bio: '',
    years_of_experience: '',
    employment_status: '',
    skills: [],
    
    // Step 3: Work Experience
    work_experiences: [],
    
    // Step 4: Education
    educations: [],
    
    // Step 5: Job Preferences
    desired_job_titles: [],
    preferred_industries: [],
    work_mode_preference: '',
    salary_min: '',
    salary_max: '',
    willing_to_relocate: false,
    notice_period: '',
  });

  const [currentSkill, setCurrentSkill] = useState({ name: '', level: 'Intermediate' });
  const [currentWorkExp, setCurrentWorkExp] = useState({
    company_name: '',
    job_title: '',
    location: '',
    start_date: '',
    end_date: '',
    is_current: false,
    description: ''
  });
  const [currentEducation, setCurrentEducation] = useState({
    institution_name: '',
    degree: '',
    field_of_study: '',
    start_date: '',
    end_date: '',
    is_current: false,
    grade: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const provinces = [
    'Gauteng', 'Western Cape', 'KwaZulu-Natal', 'Eastern Cape',
    'Free State', 'Limpopo', 'Mpumalanga', 'North West', 'Northern Cape'
  ];

  const employmentStatuses = [
    'Employed', 'Unemployed', 'Student', 'Open to opportunities'
  ];

  const workModes = ['Remote', 'Hybrid', 'On-site', 'Any'];
  const noticePeriods = ['Immediate', '1 week', '1 month', '2 months', '3 months'];
  const skillLevels = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];
  const degrees = ["Bachelor's", "Master's", "PhD", "Diploma", "Certificate", "High School"];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const addSkill = () => {
    if (currentSkill.name.trim()) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, { ...currentSkill }]
      }));
      setCurrentSkill({ name: '', level: 'Intermediate' });
    }
  };

  const removeSkill = (index) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter((_, i) => i !== index)
    }));
  };

  const addWorkExperience = () => {
    if (currentWorkExp.company_name && currentWorkExp.job_title) {
      setFormData(prev => ({
        ...prev,
        work_experiences: [...prev.work_experiences, { ...currentWorkExp }]
      }));
      setCurrentWorkExp({
        company_name: '',
        job_title: '',
        location: '',
        start_date: '',
        end_date: '',
        is_current: false,
        description: ''
      });
    }
  };

  const removeWorkExperience = (index) => {
    setFormData(prev => ({
      ...prev,
      work_experiences: prev.work_experiences.filter((_, i) => i !== index)
    }));
  };

  const addEducation = () => {
    if (currentEducation.institution_name && currentEducation.degree) {
      setFormData(prev => ({
        ...prev,
        educations: [...prev.educations, { ...currentEducation }]
      }));
      setCurrentEducation({
        institution_name: '',
        degree: '',
        field_of_study: '',
        start_date: '',
        end_date: '',
        is_current: false,
        grade: ''
      });
    }
  };

  const removeEducation = (index) => {
    setFormData(prev => ({
      ...prev,
      educations: prev.educations.filter((_, i) => i !== index)
    }));
  };

  const addJobTitle = (title) => {
    if (title.trim() && !formData.desired_job_titles.includes(title.trim())) {
      setFormData(prev => ({
        ...prev,
        desired_job_titles: [...prev.desired_job_titles, title.trim()]
      }));
    }
  };

  const removeJobTitle = (index) => {
    setFormData(prev => ({
      ...prev,
      desired_job_titles: prev.desired_job_titles.filter((_, i) => i !== index)
    }));
  };

  const handleNext = () => {
    // Validate current step
    if (currentStep === 1) {
      if (!formData.full_name || !formData.professional_title) {
        setError('Please fill in all required fields');
        return;
      }
    }
    setError('');
    setCurrentStep(prev => prev + 1);
  };

  const handleBack = () => {
    setError('');
    setCurrentStep(prev => prev - 1);
  };

  const handleSkip = () => {
    setError('');
    setCurrentStep(prev => prev + 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      // Create profile
      const profileData = {
        full_name: formData.full_name,
        phone: formData.phone,
        city: formData.city,
        province: formData.province,
        professional_title: formData.professional_title,
        bio: formData.bio,
        years_of_experience: formData.years_of_experience ? parseInt(formData.years_of_experience) : null,
        employment_status: formData.employment_status,
        // Backend expects List[str] — flatten skill objects to "Name (Level)" strings
        skills: formData.skills.map(s => `${s.name} (${s.level})`),
        desired_job_titles: formData.desired_job_titles,
        preferred_industries: formData.preferred_industries,
        work_mode_preference: formData.work_mode_preference,
        salary_min: formData.salary_min ? parseInt(formData.salary_min) : null,
        salary_max: formData.salary_max ? parseInt(formData.salary_max) : null,
        willing_to_relocate: formData.willing_to_relocate,
        notice_period: formData.notice_period,
      };

      const profileResponse = await fetch(`${API}/job-seeker-profile/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(profileData)
      });

      if (!profileResponse.ok) {
        const errorData = await profileResponse.json().catch(() => ({}));
        const detail = errorData.detail;
        // Profile already exists — that's fine, just continue
        if (detail !== 'Profile already exists') {
          if (Array.isArray(detail)) {
            throw new Error(detail.map(e => e.msg || JSON.stringify(e)).join(', '));
          }
          throw new Error(detail || `Server error (${profileResponse.status})`);
        }
      }

      if (profileResponse.status === 401 || profileResponse.status === 403) {
        setError('Your session has expired. Please log in again.');
        setTimeout(() => { localStorage.removeItem('token'); window.location.href = '/login'; }, 2000);
        return;
      }

      // Add work experiences
      for (const exp of formData.work_experiences) {
        await fetch(`${API}/job-seeker-profile/work-experience/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(exp)
        });
      }

      // Add educations
      for (const edu of formData.educations) {
        await fetch(`${API}/job-seeker-profile/education/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(edu)
        });
      }

      // Navigate to dashboard
      navigate('/dashboard');
    } catch (err) {
      console.error('Profile creation error:', err);
      setError(err.message || 'Failed to create profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="step-content">
      <h2>Basic Information</h2>
      <p className="step-description">Let's start with the basics</p>

      <div className="form-group">
        <label>Full Name *</label>
        <input
          type="text"
          name="full_name"
          value={formData.full_name}
          onChange={handleInputChange}
          placeholder="John Doe"
          required
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Phone Number</label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="+27 123 456 789"
          />
        </div>

        <div className="form-group">
          <label>Professional Title *</label>
          <input
            type="text"
            name="professional_title"
            value={formData.professional_title}
            onChange={handleInputChange}
            placeholder="e.g., Software Developer"
            required
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>City</label>
          <input
            type="text"
            name="city"
            value={formData.city}
            onChange={handleInputChange}
            placeholder="Johannesburg"
          />
        </div>

        <div className="form-group">
          <label>Province</label>
          <select
            name="province"
            value={formData.province}
            onChange={handleInputChange}
          >
            <option value="">Select Province</option>
            {provinces.map(prov => (
              <option key={prov} value={prov}>{prov}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="step-content">
      <h2>Professional Background</h2>
      <p className="step-description">Tell us about your experience</p>

      <div className="form-group">
        <label>About Me</label>
        <textarea
          name="bio"
          value={formData.bio}
          onChange={handleInputChange}
          rows="4"
          placeholder="Brief summary of your professional background..."
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Years of Experience</label>
          <input
            type="number"
            name="years_of_experience"
            value={formData.years_of_experience}
            onChange={handleInputChange}
            min="0"
            placeholder="5"
          />
        </div>

        <div className="form-group">
          <label>Employment Status</label>
          <select
            name="employment_status"
            value={formData.employment_status}
            onChange={handleInputChange}
          >
            <option value="">Select Status</option>
            {employmentStatuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-group">
        <label>Skills</label>
        <div className="skill-input-group">
          <input
            type="text"
            value={currentSkill.name}
            onChange={(e) => setCurrentSkill({ ...currentSkill, name: e.target.value })}
            placeholder="e.g., Python, Project Management"
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
          />
          <select
            value={currentSkill.level}
            onChange={(e) => setCurrentSkill({ ...currentSkill, level: e.target.value })}
          >
            {skillLevels.map(level => (
              <option key={level} value={level}>{level}</option>
            ))}
          </select>
          <button type="button" onClick={addSkill} className="btn-add">Add</button>
        </div>
        <div className="skills-list">
          {formData.skills.map((skill, index) => (
            <span key={index} className="skill-tag">
              {skill.name} ({skill.level})
              <button type="button" onClick={() => removeSkill(index)}>×</button>
            </span>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="step-content">
      <h2>Work Experience</h2>
      <p className="step-description">Add your work history (optional)</p>

      <div className="experience-form">
        <div className="form-row">
          <div className="form-group">
            <label>Company Name</label>
            <input
              type="text"
              value={currentWorkExp.company_name}
              onChange={(e) => setCurrentWorkExp({ ...currentWorkExp, company_name: e.target.value })}
              placeholder="Company Name"
            />
          </div>

          <div className="form-group">
            <label>Job Title</label>
            <input
              type="text"
              value={currentWorkExp.job_title}
              onChange={(e) => setCurrentWorkExp({ ...currentWorkExp, job_title: e.target.value })}
              placeholder="Job Title"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Location</label>
            <input
              type="text"
              value={currentWorkExp.location}
              onChange={(e) => setCurrentWorkExp({ ...currentWorkExp, location: e.target.value })}
              placeholder="City, Country"
            />
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={currentWorkExp.is_current}
                onChange={(e) => setCurrentWorkExp({ ...currentWorkExp, is_current: e.target.checked, end_date: e.target.checked ? '' : currentWorkExp.end_date })}
              />
              Currently working here
            </label>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Start Date</label>
            <input
              type="date"
              value={currentWorkExp.start_date}
              onChange={(e) => setCurrentWorkExp({ ...currentWorkExp, start_date: e.target.value })}
            />
          </div>

          {!currentWorkExp.is_current && (
            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                value={currentWorkExp.end_date}
                onChange={(e) => setCurrentWorkExp({ ...currentWorkExp, end_date: e.target.value })}
              />
            </div>
          )}
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            value={currentWorkExp.description}
            onChange={(e) => setCurrentWorkExp({ ...currentWorkExp, description: e.target.value })}
            rows="3"
            placeholder="Describe your responsibilities and achievements..."
          />
        </div>

        <button type="button" onClick={addWorkExperience} className="btn-add-item">
          <i className="fas fa-plus"></i> Add Work Experience
        </button>
      </div>

      <div className="added-items">
        {formData.work_experiences.map((exp, index) => (
          <div key={index} className="item-card">
            <div className="item-header">
              <h4>{exp.job_title}</h4>
              <button type="button" onClick={() => removeWorkExperience(index)} className="btn-remove">
                <i className="fas fa-trash"></i>
              </button>
            </div>
            <p className="item-company">{exp.company_name}</p>
            <p className="item-date">
              {new Date(exp.start_date).toLocaleDateString()} - {exp.is_current ? 'Present' : new Date(exp.end_date).toLocaleDateString()}
            </p>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="step-content">
      <h2>Education</h2>
      <p className="step-description">Add your educational background (optional)</p>

      <div className="education-form">
        <div className="form-row">
          <div className="form-group">
            <label>Institution Name</label>
            <input
              type="text"
              value={currentEducation.institution_name}
              onChange={(e) => setCurrentEducation({ ...currentEducation, institution_name: e.target.value })}
              placeholder="University/College Name"
            />
          </div>

          <div className="form-group">
            <label>Degree</label>
            <select
              value={currentEducation.degree}
              onChange={(e) => setCurrentEducation({ ...currentEducation, degree: e.target.value })}
            >
              <option value="">Select Degree</option>
              {degrees.map(deg => (
                <option key={deg} value={deg}>{deg}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Field of Study</label>
            <input
              type="text"
              value={currentEducation.field_of_study}
              onChange={(e) => setCurrentEducation({ ...currentEducation, field_of_study: e.target.value })}
              placeholder="e.g., Computer Science"
            />
          </div>

          <div className="form-group">
            <label>Grade/GPA (optional)</label>
            <input
              type="text"
              value={currentEducation.grade}
              onChange={(e) => setCurrentEducation({ ...currentEducation, grade: e.target.value })}
              placeholder="e.g., 3.8 GPA"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Start Date</label>
            <input
              type="date"
              value={currentEducation.start_date}
              onChange={(e) => setCurrentEducation({ ...currentEducation, start_date: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={currentEducation.is_current}
                onChange={(e) => setCurrentEducation({ ...currentEducation, is_current: e.target.checked, end_date: e.target.checked ? '' : currentEducation.end_date })}
              />
              Currently studying
            </label>
          </div>
        </div>

        {!currentEducation.is_current && (
          <div className="form-group">
            <label>End Date</label>
            <input
              type="date"
              value={currentEducation.end_date}
              onChange={(e) => setCurrentEducation({ ...currentEducation, end_date: e.target.value })}
            />
          </div>
        )}

        <button type="button" onClick={addEducation} className="btn-add-item">
          <i className="fas fa-plus"></i> Add Education
        </button>
      </div>

      <div className="added-items">
        {formData.educations.map((edu, index) => (
          <div key={index} className="item-card">
            <div className="item-header">
              <h4>{edu.degree} in {edu.field_of_study}</h4>
              <button type="button" onClick={() => removeEducation(index)} className="btn-remove">
                <i className="fas fa-trash"></i>
              </button>
            </div>
            <p className="item-company">{edu.institution_name}</p>
            <p className="item-date">
              {new Date(edu.start_date).toLocaleDateString()} - {edu.is_current ? 'Present' : new Date(edu.end_date).toLocaleDateString()}
            </p>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="step-content">
      <h2>Job Preferences</h2>
      <p className="step-description">What are you looking for?</p>

      <div className="form-group">
        <label>Desired Job Titles</label>
        <input
          type="text"
          placeholder="Type a job title and press Enter"
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addJobTitle(e.target.value);
              e.target.value = '';
            }
          }}
          onBlur={(e) => {
            if (e.target.value.trim()) {
              e.target.value.split(',').forEach(t => addJobTitle(t.trim()));
              e.target.value = '';
            }
          }}
        />
        <div className="tags-list">
          {formData.desired_job_titles.map((title, index) => (
            <span key={index} className="tag">
              {title}
              <button type="button" onClick={() => removeJobTitle(index)}>×</button>
            </span>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label>Work Mode Preference</label>
        <select
          name="work_mode_preference"
          value={formData.work_mode_preference}
          onChange={handleInputChange}
        >
          <option value="">Select Preference</option>
          {workModes.map(mode => (
            <option key={mode} value={mode}>{mode}</option>
          ))}
        </select>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Minimum Salary (ZAR)</label>
          <input
            type="number"
            name="salary_min"
            value={formData.salary_min}
            onChange={handleInputChange}
            placeholder="30000"
          />
        </div>

        <div className="form-group">
          <label>Maximum Salary (ZAR)</label>
          <input
            type="number"
            name="salary_max"
            value={formData.salary_max}
            onChange={handleInputChange}
            placeholder="50000"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Notice Period</label>
          <select
            name="notice_period"
            value={formData.notice_period}
            onChange={handleInputChange}
          >
            <option value="">Select Notice Period</option>
            {noticePeriods.map(period => (
              <option key={period} value={period}>{period}</option>
            ))}
          </select>
        </div>

        <div className="form-group checkbox-group">
          <label>
            <input
              type="checkbox"
              name="willing_to_relocate"
              checked={formData.willing_to_relocate}
              onChange={handleInputChange}
            />
            Willing to relocate
          </label>
        </div>
      </div>
    </div>
  );

  return (
    <div className="profile-setup-container">
      <div className="setup-header">
        <div className="logo">
          <img src="/JobLinkedAI.png" alt="JobLinked AI" />
          <span>JobLinked AI</span>
        </div>
        <div className="progress-bar">
          <div className="progress" style={{ width: `${(currentStep / 5) * 100}%` }}></div>
        </div>
        <p className="step-indicator">Step {currentStep} of 5</p>
      </div>

      <div className="setup-content">
        <form onSubmit={(e) => e.preventDefault()}>
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
          {currentStep === 5 && renderStep5()}

          {error && <div className="error-message">{error}</div>}

          <div className="form-actions">
            {currentStep > 1 && (
              <button type="button" onClick={handleBack} className="btn-secondary">
                <i className="fas fa-arrow-left"></i> Back
              </button>
            )}

            <div className="right-actions">
              {currentStep > 2 && currentStep < 5 && (
                <button type="button" onClick={handleSkip} className="btn-skip">
                  Skip
                </button>
              )}

              {currentStep < 5 ? (
                <button type="button" onClick={handleNext} className="btn-primary">
                  Next <i className="fas fa-arrow-right"></i>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Creating Profile...' : 'Complete Setup'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default JobSeekerProfileSetup;
